import { Button } from "@/components/ui/button";
import {
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import { UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { UserRole } from "@common/utils/graphql";
import { PersonManualForm } from "./PersonManualForm";
import { PersonUploadForm } from "./PersonUploadForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { CategoryUploadSchema } from "../categories/forms";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import { EmployeeFormSchema } from "../forms/Forms";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { useRecoilValue } from "recoil";
import { roleState } from "@common/state/roleState";
import { ConfirmationDialog } from "@common/components/ConfirmationDialog/ConfirmationDialog";
import {
  useInviteOrUpdateUser,
  useTerminateEmployeeProfile,
  useUploadUser,
} from "./hooks";
import {
  SheetTabsContainer,
  SheetTabsContent,
  SheetTabsList,
} from "@common/components";
import { hasDirtyFields } from "@/utils/utils";

interface Props {
  onBack: () => void;
  mode?: "edit" | "create";
  userId?: string;
  form: UseFormReturn<z.infer<typeof EmployeeFormSchema>>;
}

const SheetTabs = [
  {
    name: "Input Manually",
    value: "manual",
  },
  {
    name: "Upload from CSV",
    value: "upload",
  },
];

export const InviteOrEditPersonContent = ({
  form,
  onBack,
  userId,
  mode,
}: Props) => {
  const isEditMode = mode === "edit";
  const role = useRecoilValue(roleState);
  const currentUserId = useUserStore((state) => state.userId);
  const uploadForm = useForm<z.infer<typeof CategoryUploadSchema>>({
    resolver: zodResolver(CategoryUploadSchema),
  });

  usePreventSheetClose(() => hasDirtyFields(form));
  const [inviteOrUpdateUser, { loading: isLoading }] = useInviteOrUpdateUser({
    variables: { userId },
    onSucces: onBack,
    form,
  });
  const [terminateEmployeeProfile, { loading: isTerminating }] =
    useTerminateEmployeeProfile({
      variables: { userId: userId! },
      onSucces: onBack,
    });
  const [uploadPersonCSV, { loading: isUploading }] = useUploadUser({
    onSucces: onBack,
    form: uploadForm,
  });

  const header = (
    <>
      <SheetBackButton onClick={onBack} />
      <SheetHeader>
        <SheetTitle>{isEditMode ? "Edit User" : "Invite People"}</SheetTitle>
      </SheetHeader>
    </>
  );

  return (
    <>
      {header}
      <SheetTabsContainer defaultValue="manual">
        {!isEditMode && <SheetTabsList tabs={SheetTabs} />}
        <SheetTabsContent value="manual">
          <PersonManualForm
            form={form}
            isEditMode={isEditMode}
            isLoading={isLoading}
            userId={userId}
          />
          <div className="min-h-8 flex-1" />
          {isEditMode ? (
            <>
              {role === UserRole.Admin && currentUserId !== userId && (
                <ConfirmationDialog
                  title="Confirm User Termination"
                  description={`Are you sure you want to terminate this user?\nThis action cannot be undone.`}
                  onConfirm={terminateEmployeeProfile}
                  confirmText={"Terminate User"}
                  disabled={isTerminating || isLoading}
                  asChild
                >
                  <Button
                    variant="destructive"
                    disabled={isTerminating || isLoading}
                    className="mb-4"
                  >
                    {isTerminating ? "Terminating User..." : "Terminate User"}
                  </Button>
                </ConfirmationDialog>
              )}
              <Button
                onClick={() => inviteOrUpdateUser(true)}
                disabled={isTerminating || isLoading}
              >
                {isLoading ? "Saving User..." : "Save User"}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => inviteOrUpdateUser(false)}
              disabled={isLoading}
            >
              {isLoading ? "Inviting New Person..." : "Invite New Person"}
            </Button>
          )}
        </SheetTabsContent>
        <SheetTabsContent value="upload">
          <PersonUploadForm form={uploadForm} loading={isUploading} />
          <div className="min-h-8 flex-1" />
          <Button onClick={uploadPersonCSV} disabled={isUploading}>
            {isUploading ? "Uploading CSV" : "Upload CSV"}
          </Button>
        </SheetTabsContent>
      </SheetTabsContainer>
    </>
  );
};
